import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight, BadgeCheck } from "lucide-react";
import NexoriqLogo from "./NexoriqLogo";
import { trackRFQClick } from "@/lib/analytics";

const NAV = [
  { to: "/", label: "Home", num: "01" },
  { to: "/capabilities", label: "Capabilities", num: "02" },
  { to: "/what-we-source", label: "What We Source", num: "03" },
  { to: "/industries", label: "Industries", num: "04" },
  { to: "/certifications", label: "Certifications", num: "05" },
  { to: "/partners", label: "Partners", num: "06" },
  { to: "/corporate", label: "CSR", num: "07" },
  { to: "/about", label: "About", num: "08" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <>
      <header
        data-testid="site-header"
        className={
          "fixed top-0 left-0 right-0 z-40 transition-all duration-300 " +
          (scrolled
            ? "bg-nx-bg/95 backdrop-blur-md border-b border-nx-line"
            : "bg-nx-bg border-b border-transparent")
        }
      >
        <div className="px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" data-testid="header-logo-link" className="flex items-center shrink-0">
              <NexoriqLogo />
            </Link>
            <Link
              to="/certifications"
              data-testid="header-iso-badge"
              className="flex lg:hidden xl:flex items-center gap-1.5 hover:opacity-80 transition-opacity"
              title="ISO 9001:2015 Quality Management System certified"
            >
              <BadgeCheck size={13} strokeWidth={2.2} className="text-nx-accent shrink-0" />
              <span className="font-mono text-[0.55rem] uppercase tracking-[0.12em] text-nx-navy whitespace-nowrap leading-none">
                ISO 9001:2015<span className="hidden sm:inline"> Certified</span>
              </span>
            </Link>
          </div>

          <nav className="hidden lg:flex items-center gap-6" aria-label="Primary">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  "font-ibm text-[0.78rem] uppercase tracking-[0.14em] nx-underline transition-colors " +
                  (isActive
                    ? "text-nx-navy font-medium"
                    : "text-nx-navy2 hover:text-nx-accent")
                }
              >
                <span className="text-nx-steel font-mono text-[0.65rem] mr-1.5">{n.num}</span>
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/sourcing-request"
              data-testid="header-rfq-cta"
              onClick={() => trackRFQClick("header")}
              className="max-md:!hidden md:inline-flex items-center gap-2 nx-btn"
            >
              Submit a Requirement <ArrowUpRight size={14} strokeWidth={2.2} />
            </Link>
            <button
              className="lg:hidden p-2 border border-nx-navy"
              onClick={() => setOpen((v) => !v)}
              aria-label="Toggle menu"
              data-testid="mobile-menu-toggle"
            >
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu */}
      <div
        className={
          "fixed inset-0 z-30 bg-nx-bg overflow-y-auto transition-transform duration-500 lg:hidden " +
          (open ? "translate-y-0" : "-translate-y-full invisible")
        }
        data-testid="mobile-menu"
      >
        <div className="pt-24 px-6">
          <ul className="space-y-4">
            {NAV.map((n) => (
              <li key={n.to} className="border-b border-nx-line pb-4">
                <Link
                  to={n.to}
                  onClick={() => setOpen(false)}
                  className="flex items-baseline justify-between font-cabinet text-4xl font-black tracking-tighter text-nx-navy"
                >
                  <span>
                    <span className="text-nx-steel font-mono text-sm mr-3">{n.num}</span>
                    {n.label}
                  </span>
                  <ArrowUpRight size={22} />
                </Link>
              </li>
            ))}
          </ul>
          <Link
            to="/sourcing-request"
            onClick={() => { setOpen(false); trackRFQClick("mobile"); }}
            className="nx-btn mt-10"
            data-testid="mobile-rfq-cta"
          >
            Submit a Requirement <ArrowUpRight size={14} />
          </Link>
          <div className="mt-8 flex items-center gap-2 text-nx-navy" data-testid="mobile-iso-badge">
            <BadgeCheck size={16} strokeWidth={2.2} className="text-nx-accent" />
            <span className="font-mono text-[0.62rem] uppercase tracking-[0.16em]">
              ISO 9001:2015 Quality Management System Certified
            </span>
          </div>
        </div>
      </div>

      <div className="h-16" aria-hidden="true" />
    </>
  );
}
